import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { accountService, TokenManager, SessionManager as SessionUtils } from '../../services/api/accountService';
import { isApiError, getErrorDetails } from '../../services/api/client';
import type { AuthState } from '../../types/account';

// Session Context Interface
interface SessionContextType extends AuthState {
  login: (token: string) => Promise<void>;
  logout: () => void;
  switchAccount: (accountId: string) => void;
  refreshAccounts: () => Promise<void>;
  extendSession: () => Promise<boolean>;
  timeUntilExpiry: number | null;
}

// Create Session Context
const SessionContext = createContext<SessionContextType | undefined>(undefined);

// Session Provider Props
interface SessionProviderProps {
  children: React.ReactNode;
  onSessionWarning?: () => void;
  onSessionExpiry?: () => void;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ 
  children, 
  onSessionWarning,
  onSessionExpiry 
}) => {
  // Authentication State
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
    accounts: [],
    currentAccount: null,
    primaryAccountId: null,
    sessionExpiry: null,
    loading: true,
    error: null,
  });

  // Session timeout state
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);

  // Initialize session on mount - only once
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      if (mounted) {
        await initializeSession();
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Session timeout monitoring
  useEffect(() => {
    if (authState.isAuthenticated && authState.sessionExpiry) {
      // Start session monitoring
      SessionUtils.startSessionMonitoring(
        () => {
          onSessionWarning?.();
        },
        () => {
          logout();
          onSessionExpiry?.();
        }
      );

      // Update time until expiry every minute
      const intervalId = setInterval(() => {
        const remaining = SessionUtils.getTimeUntilExpiry();
        setTimeUntilExpiry(remaining);
      }, 60000); // Update every minute

      return () => {
        clearInterval(intervalId);
        SessionUtils.clearSessionTimers();
      };
    }

    return () => {
      SessionUtils.clearSessionTimers();
    };
  }, [authState.isAuthenticated, authState.sessionExpiry, onSessionWarning, onSessionExpiry]);

  // Initialize session from storage or auto-login
  const initializeSession = useCallback(async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Check if token exists and is valid
      const isInitialized = TokenManager.initialize();
      
      if (isInitialized) {
        const token = TokenManager.getToken();
        const expiry = TokenManager.getTokenExpiry();
        
        // Try to get cached account data first
        const cachedData = TokenManager.getAccountData();
        
        if (cachedData && cachedData.accounts.length > 0) {
          // Use cached data and set up session
          const currentAccount = cachedData.accounts.find(
            acc => acc.accountId === cachedData.primaryAccountId
          ) || cachedData.accounts[0] || null;

          setAuthState({
            isAuthenticated: true,
            token,
            accounts: cachedData.accounts,
            currentAccount,
            primaryAccountId: cachedData.primaryAccountId,
            sessionExpiry: expiry,
            loading: false,
            error: null,
          });
        } else {
          // No cached data, fetch fresh accounts
          await fetchAccounts(token, expiry);
        }
      } else {
        // No valid token - automatically create session with demo token
        await autoStartDemoSession();
      }
    } catch (error) {
      console.error('Failed to initialize session:', error);
      
      // Always try demo session as fallback
      try {
        await autoStartDemoSession();
      } catch (demoError) {
        console.error('Demo session also failed:', demoError);
        const errorDetails = getErrorDetails(error);
        
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: false,
          loading: false,
          error: errorDetails.message,
        }));
      }
    }
  }, []); // Remove dependencies to ensure it runs

  // Auto-start demo session
  const autoStartDemoSession = async () => {
    // Create a proper demo JWT token with valid expiry (24 hours from now)
    const currentTime = Math.floor(Date.now() / 1000);
    const expiryTime = currentTime + (24 * 60 * 60); // 24 hours
    
    // Create a proper base64 encoded JWT token with proper padding
    const header = btoa(JSON.stringify({ alg: "HS256", typ: "JWT" })).replace(/=/g, '');
    const payload = btoa(JSON.stringify({
      sub: "demo_user_12345",
      name: "Demo User",
      iat: currentTime,
      exp: expiryTime
    })).replace(/=/g, '');
    const signature = "demo_signature"; // In real implementation this would be properly signed
    
    const demoToken = `${header}.${payload}.${signature}`;
    
    // Set token and get expiry
    TokenManager.setToken(demoToken);
    const expiry = TokenManager.getTokenExpiry();

    // Fetch accounts
    await fetchAccounts(demoToken, expiry);
  };

  // Fetch accounts from API
  const fetchAccounts = async (token: string | null, expiry: number | null) => {
    try {
      const accountsResponse = await accountService.getAccounts();
      const currentAccount = accountsResponse.accounts.find(
        acc => acc.accountId === accountsResponse.primaryAccountId
      ) || accountsResponse.accounts[0] || null;

      // Cache account data
      TokenManager.setAccountData(accountsResponse.accounts, accountsResponse.primaryAccountId);

      setAuthState({
        isAuthenticated: true,
        token,
        accounts: accountsResponse.accounts,
        currentAccount,
        primaryAccountId: accountsResponse.primaryAccountId,
        sessionExpiry: expiry,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      const errorDetails = getErrorDetails(error);
      
      // Handle authentication errors
      if (isApiError(error) && error.status === 401) {
        TokenManager.clearToken();
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: false,
          loading: false,
          error: 'Session expired. Please login again.',
        }));
      } else {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: errorDetails.message || 'Failed to load account information',
        }));
      }
    }
  };


  // Login with token
  const login = useCallback(async (token: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Set token and get expiry
      TokenManager.setToken(token);
      const expiry = TokenManager.getTokenExpiry();

      // Fetch accounts
      await fetchAccounts(token, expiry);
    } catch (error) {
      console.error('Login failed:', error);
      const errorDetails = getErrorDetails(error);
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorDetails.message || 'Login failed',
      }));
      
      TokenManager.clearToken();
      throw error;
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    SessionUtils.logout();
    setAuthState({
      isAuthenticated: false,
      token: null,
      accounts: [],
      currentAccount: null,
      primaryAccountId: null,
      sessionExpiry: null,
      loading: false,
      error: null,
    });
    setTimeUntilExpiry(null);
  }, []);

  // Switch current account
  const switchAccount = useCallback((accountId: string) => {
    const account = authState.accounts.find(acc => acc.accountId === accountId);
    if (account) {
      setAuthState(prev => ({
        ...prev,
        currentAccount: account,
      }));
    }
  }, [authState.accounts]);

  // Refresh accounts
  const refreshAccounts = useCallback(async () => {
    if (!authState.isAuthenticated) return;

    setAuthState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const accountsResponse = await accountService.getAccounts();
      const currentAccount = accountsResponse.accounts.find(
        acc => acc.accountId === (authState.currentAccount?.accountId || accountsResponse.primaryAccountId)
      ) || accountsResponse.accounts[0] || null;

      TokenManager.setAccountData(accountsResponse.accounts, accountsResponse.primaryAccountId);

      setAuthState(prev => ({
        ...prev,
        accounts: accountsResponse.accounts,
        primaryAccountId: accountsResponse.primaryAccountId,
        currentAccount,
        loading: false,
      }));
    } catch (error) {
      console.error('Failed to refresh accounts:', error);
      const errorDetails = getErrorDetails(error);
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorDetails.message,
      }));
    }
  }, [authState.isAuthenticated, authState.currentAccount?.accountId]);

  // Extend session
  const extendSession = useCallback(async () => {
    return await SessionUtils.extendSession();
  }, []);

  // Session context value
  const contextValue: SessionContextType = {
    ...authState,
    login,
    logout,
    switchAccount,
    refreshAccounts,
    extendSession,
    timeUntilExpiry,
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

// Session hook
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

// Session Manager Component (for manual control)
export const SessionManager: React.FC = () => {
  const session = useSession();

  // Auto-hide if not authenticated
  if (!session.isAuthenticated) {
    return null;
  }

  return (
    <div className="session-manager" style={{ display: 'none' }}>
      {/* Hidden session manager for internal state management */}
      {/* This component manages session state but doesn't render UI */}
      {/* UI components can use the useSession hook to access session data */}
    </div>
  );
};

export default SessionManager;
